<template>
  <div class="time-display" :class="[theme, { 'animate': animate }]">
    <div class="time-section">
      <div class="time flex">
        <div class="time1">
          {{ formattedTime }}
        </div>
        <div class="time2">
          {{ ampm }}
        </div>
      </div>

      <div class="date">{{ formattedDate }}</div>
    </div>
    <div v-if="showDay" class="day-section">
      <div class="day">{{ dayOfWeek }}</div>
      <div v-if="showGreeting" class="greeting">{{ greeting }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimeDisplay',
  props: {
    theme: {
      type: String,
      default: 'light', // 'light', 'dark', 'minimal', 'elegant'
      validator: value => ['light', 'dark', 'minimal', 'elegant'].includes(value)
    },
    showSeconds: {
      type: Boolean,
      default: true
    },
    showDay: {
      type: Boolean,
      default: true
    },
    showGreeting: {
      type: Boolean,
      default: true
    },
    animate: {
      type: Boolean,
      default: true
    },
    timeFormat: {
      type: String,
      default: '24', // '24' or '12'
      validator: value => ['24', '12'].includes(value)
    },
    dateFormat: {
      type: String,
      default: 'long' // 'long', 'short', 'numeric'
    }
  },
  data() {
    return {
      currentTime: new Date(),
      timer: null,
      ampm:""
    }
  },
  computed: {
    formattedTime() {
      let hours = this.currentTime.getHours()
      let minutes = this.currentTime.getMinutes()
      const seconds = this.currentTime.getSeconds()
      
      // Format for 12-hour clock
      if (this.timeFormat === '12') {
        this.ampm = hours >= 12 ? 'PM' : 'AM'
        hours = hours % 12
        hours = hours ? hours : 12 // the hour '0' should be '12'
        
        if (this.showSeconds) {
          return `${this.padZero(hours)}:${this.padZero(minutes)}:${this.padZero(seconds)} `
        }
        return `${this.padZero(hours)}:${this.padZero(minutes)} `
      }
      
      // Format for 24-hour clock
      if (this.showSeconds) {
        return `${this.padZero(hours)}:${this.padZero(minutes)}:${this.padZero(seconds)}`
      }
      return `${this.padZero(hours)}:${this.padZero(minutes)}`
    },
    formattedDate() {
      const options = {}
      if (this.dateFormat === 'long') {
        options.year = 'numeric'
        options.month = 'long'
        options.day = 'numeric'
      } else if (this.dateFormat === 'short') {
        options.year = 'numeric'
        options.month = 'short'
        options.day = 'numeric'
      } else {
        options.year = 'numeric'
        options.month = 'numeric'
        options.day = 'numeric'
      }
      
      return this.currentTime.toLocaleDateString(undefined, options)
    },
    dayOfWeek() {
      return this.currentTime.toLocaleDateString(undefined, { weekday: 'long' })
    },
    greeting() {
      const hour = this.currentTime.getHours()
      if (hour < 12) return 'Good Morning'
      if (hour < 18) return 'Good Afternoon'
      return 'Good Evening'
    }
  },
  mounted() {
    this.updateTime()
    this.timer = setInterval(this.updateTime, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    updateTime() {
      this.currentTime = new Date()
    },
    padZero(num) {
      return num < 10 ? `0${num}` : num
    }
  }
}
</script>

<style scoped>
.time-display {
  font-family: 'Arial', sans-serif;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
}

.time-display.animate:hover {
  transform: scale(1.02);
}

.time-section {
  text-align: center;
  margin-right: 40px;
  min-width: 300px;
  
}

.time {
  font-size: 45px;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 5px;
  .time1{
    min-width: 220px;
  }
}

.date {
  font-size: 1rem;
  opacity: 0.8;
}

.day-section {
  text-align: center;
}

.day {
  font-size: 1.2rem;
  font-weight: bold;
}

.greeting {
  font-size: 0.9rem;
  opacity: 0.7;
  margin-top: 5px;
}

/* Light Theme (Default) */
.light {
  color: #333;
}

.light .time {
  color: #2c3e50;
}

.light .date {
  color: #7f8c8d;
}

/* Dark Theme */
.dark {
  color: #fff;
  background-color: #2c3e50;
  padding: 15px;
  border-radius: 10px;
}

.dark .time {
  color: #ecf0f1;
}

.dark .date {
  color: #bdc3c7;
}

/* Minimal Theme */
.minimal {
  color: #333;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.minimal .time {
  font-size: 2.2rem;
  letter-spacing: 1px;
  color: #333;
}

.minimal .date {
  font-size: 0.9rem;
  letter-spacing: 1px;
  color: #666;
}

.minimal .day {
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #666;
}

/* Elegant Theme */
.elegant {
  color: #555;
  font-family: 'Georgia', serif;
}

.elegant .time {
  font-size: 45px;
  font-weight: normal;
  color: #333;
}

.elegant .date {
  font-size: 18px;
  font-style: italic;
  color: #fff;
}

.elegant .day {
  font-size: 35px;
  color: #555;
}

.elegant .greeting {
  font-size:18px;
  color: #fff;
}
</style>